import React from "react";
import { useHistory } from "react-router";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "features/authConfig";
import * as paths from "features/routing/paths";
import { LoginRedirectToast } from "features/patientDashboard/ErrorToast";
import { FullScreenLoader } from "components/FullScreenLoader/FullScreenLoader";

/**
 * RootLanding controls behavior for "/"
 *
 * We want "/" to behave like clicking "Portal Login":
 * - If already authenticated, go directly to /selector
 * - Otherwise, trigger MSAL loginRedirect
 *
 * IMPORTANT:
 * - Never call loginRedirect while MSAL is in progress
 * - Never spam loginRedirect on re-renders (attempt once per short window)
 */
const LOGIN_ATTEMPT_KEY = "ciam:root_login_attempted_at";
const LOGIN_ATTEMPT_WINDOW_MS = 30_000;

function canAttemptRootLoginOnce(): boolean {
  const now = Date.now();
  const last = Number(sessionStorage.getItem(LOGIN_ATTEMPT_KEY) || "0");
  if (last && now - last < LOGIN_ATTEMPT_WINDOW_MS) return false;
  sessionStorage.setItem(LOGIN_ATTEMPT_KEY, String(now));
  return true;
}

const RootLanding: React.FC = () => {
  const { instance, inProgress } = useMsal();
  const history = useHistory();
  const [showLoginToast, setShowLoginToast] = React.useState(false);

  const handleRetry = () => {
    sessionStorage.removeItem(LOGIN_ATTEMPT_KEY);
    setShowLoginToast(false);
    window.location.reload();
  };

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.pathname !== "/") return;

    setShowLoginToast(false);

    // 1) Wait until MSAL is idle
    if (inProgress !== InteractionStatus.None) {
      setShowLoginToast(true);
      return;
    }

    // 2) If already authenticated (any account), go to selector
    const accounts = instance.getAllAccounts();
    if (accounts.length > 0) {
      if (!instance.getActiveAccount()) {
        instance.setActiveAccount(accounts[0]);
      }
      history.replace(paths.patientSelector());
      return;
    }

    // 3) Attempt login only once per window to avoid infinite loops
    if (!canAttemptRootLoginOnce()) {
      setShowLoginToast(true);
      return;
    }

    setShowLoginToast(false);

    // After login, route to selector deterministically
    sessionStorage.setItem("postLoginRedirect", paths.patientSelector());

    instance.loginRedirect(loginRequest).catch((e) => {
      // eslint-disable-next-line no-console
      console.log("Root loginRedirect failed", e);
    });
  }, [instance, inProgress, history]);

  // Blank shell
  return (
    <>
      <FullScreenLoader />
      <LoginRedirectToast
        isLoading={showLoginToast}
        onDismiss={() => setShowLoginToast(false)}
        onRetry={handleRetry}
      />
    </>
  );
};

export default RootLanding;
